import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Evaluation, EvaluatorApplicationDetails } from '../../types/evaluator';
import Button from 'shared/components/UC/Button';
import Alert from 'shared/components/UC/Alert';
import styles from './EvaluationPanel.module.css';
import EvaluationScaleModal from './EvaluationScaleModal';

interface EvaluationPanelProps {
  application: EvaluatorApplicationDetails;
  evaluation: Evaluation | null;
}

const evaluationCriteria = [
  {
    id: 'originality_risk',
    name: 'Originalidad y riesgo de la investigación',
    weight: 20,
    description:
      'Evalúa la novedad, innovación y nivel de riesgo académico del proyecto de investigación propuesto. Considera la originalidad de la hipótesis, metodología y enfoque.',
    aspects: [
      'Novedad e innovación de la hipótesis o pregunta de investigación',
      'Originalidad de la metodología propuesta',
      'Nivel de riesgo académico asumido',
      'Diferenciación respecto al estado del arte',
      'Potencial para generar conocimiento disruptivo',
    ],
    considerations: [
      'Evalúe si el proyecto propone ideas verdaderamente nuevas o enfoques innovadores',
      'Considere el equilibrio entre innovación y viabilidad',
      'Analice si el riesgo asumido está justificado por el potencial impacto',
      'Revise la revisión bibliográfica para validar la originalidad',
    ],
  },
  {
    id: 'national_relevance',
    name: 'Pertinencia nacional e impacto potencial',
    weight: 20,
    description:
      'Analiza la relevancia del proyecto para el contexto nacional chileno y su potencial impacto en el desarrollo científico, tecnológico, social o económico del país.',
    aspects: [
      'Relevancia para el contexto chileno específico',
      'Potencial impacto en el desarrollo nacional',
      'Alineación con prioridades científicas del país',
      'Contribución al desarrollo social, económico o tecnológico',
      'Aplicabilidad de resultados en el contexto local',
    ],
    considerations: [
      'Evalúe la pertinencia del proyecto para Chile y sus desafíos específicos',
      'Considere el impacto potencial en políticas públicas o desarrollo sectorial',
      'Analice la capacidad del proyecto para abordar necesidades nacionales',
      'Revise la alineación con estrategias de desarrollo nacional',
    ],
  },
  {
    id: 'global_scalability',
    name: 'Escalabilidad global de la idea del proyecto',
    weight: 15,
    description:
      'Evalúa el potencial del proyecto para expandirse o aplicarse en contextos internacionales, considerando su relevancia y aplicabilidad global.',
    aspects: [
      'Potencial de aplicación internacional',
      'Relevancia para contextos globales similares',
      'Transferibilidad de metodologías y resultados',
      'Contribución al conocimiento global',
      'Escalabilidad de soluciones propuestas',
    ],
    considerations: [
      'Evalúe si los resultados serán aplicables en otros países o contextos',
      'Considere la capacidad de generalización de los hallazgos',
      'Analice el potencial de colaboración internacional futura',
      'Revise la relevancia para comunidades científicas internacionales',
    ],
  },
  {
    id: 'international_collaboration',
    name: 'Nivel de colaboración internacional y levantamiento de fondos adicionales',
    weight: 20,
    description:
      'Considera la capacidad del equipo para establecer colaboraciones internacionales y obtener financiamiento adicional de fuentes externas.',
    aspects: [
      'Capacidad demostrada para colaboraciones internacionales',
      'Redes y contactos internacionales del equipo',
      'Estrategias para obtener financiamiento adicional',
      'Experiencia previa en proyectos internacionales',
      'Potencial para establecer nuevas alianzas',
    ],
    considerations: [
      'Revise el historial de colaboraciones internacionales del equipo',
      'Evalúe las estrategias propuestas para buscar fondos adicionales',
      'Considere la calidad y relevancia de las redes internacionales',
      'Analice la factibilidad de las colaboraciones planteadas',
    ],
  },
  {
    id: 'technical_viability',
    name: 'Viabilidad técnica de la propuesta, planificación',
    weight: 10,
    description:
      'Evalúa la factibilidad técnica del proyecto, la calidad de la planificación, cronograma y la disponibilidad de recursos necesarios.',
    aspects: [
      'Factibilidad técnica de la metodología propuesta',
      'Disponibilidad y acceso a recursos necesarios',
      'Calidad de la planificación temporal',
      'Identificación y gestión de riesgos',
      'Coherencia entre objetivos, metodología y recursos',
    ],
    considerations: [
      'Evalúe si la metodología es técnicamente realizable',
      'Revise la disponibilidad de equipos, software y recursos humanos',
      'Analice la coherencia del cronograma propuesto',
      'Considere la experiencia del equipo con las técnicas propuestas',
    ],
  },
  {
    id: 'results_quality',
    name: 'Calidad de los resultados',
    weight: 15,
    description:
      'Analiza la calidad esperada de los resultados, productos entregables, publicaciones y contribuciones científicas del proyecto.',
    aspects: [
      'Calidad esperada de productos y entregables',
      'Potencial de publicaciones en revistas de alto impacto',
      'Contribución al avance del conocimiento científico',
      'Calidad de la transferencia de conocimiento',
      'Impacto esperado en la comunidad científica',
    ],
    considerations: [
      'Evalúe el potencial de los resultados para generar publicaciones de calidad',
      'Considere la capacidad del equipo para comunicar y difundir resultados',
      'Analice la contribución esperada al avance del campo de estudio',
      'Revise los mecanismos propuestos para la transferencia de conocimiento',
    ],
  },
];

export const COMMENT_MIN_LENGTH = 300;
export const COMMENT_MAX_LENGTH = 2000;
export const BUDGET_COMMENT_MAX_LENGTH = 1000;

const EvaluationPanel: React.FC<EvaluationPanelProps> = ({
  application,
  evaluation,
}) => {
  const {
    register,
    watch,
    formState: { errors, touchedFields },
  } = useFormContext();
  const [showScaleModal, setShowScaleModal] = useState(false);
  const [expandedCriterionId, setExpandedCriterionId] = useState<string | null>(
    null
  );

  const watchedValues = watch();
  const criteriaEvaluations = watchedValues.criteriaEvaluations || {};

  // Calculate completion progress
  const completedCriteria = evaluationCriteria.filter((criterion) => {
    const evaluation = criteriaEvaluations[criterion.id];
    return (
      evaluation &&
      evaluation.score !== undefined &&
      evaluation.score !== null &&
      evaluation.score !== '' &&
      evaluation.comments &&
      evaluation.comments.trim() !== '' &&
      evaluation.comments.trim().length >= COMMENT_MIN_LENGTH &&
      evaluation.comments.trim().length <= COMMENT_MAX_LENGTH
    );
  }).length;

  const totalCriteria = evaluationCriteria.length;
  const progressPercentage = (completedCriteria / totalCriteria) * 100;

  const getCharacterCountClass = (length: number) => {
    // if (length < COMMENT_MIN_LENGTH) return styles.countBelowMin;
    // if (length > COMMENT_MAX_LENGTH) return styles.countAboveMax;
    // if (length > COMMENT_MAX_LENGTH - 100) return styles.countNearMax;
    return styles.countValid;
  };

  const getTextareaClass = (
    length: number,
    hasError: boolean,
    isTouched: boolean
  ) => {
    const baseClass = styles.commentsInput;
    const errorClass = hasError && isTouched ? styles.inputError : '';
    const lengthErrorClass =
      isTouched && (length < COMMENT_MIN_LENGTH || length > COMMENT_MAX_LENGTH)
        ? styles.textareaError
        : '';

    return `${baseClass} ${errorClass} ${lengthErrorClass}`.trim();
  };

  const getCharacterCountText = (length: number, isBudget: boolean = false) => {
    return isBudget
      ? `${length}/${BUDGET_COMMENT_MAX_LENGTH} caracteres`
      : `${length}/${COMMENT_MAX_LENGTH} caracteres (mín: ${COMMENT_MIN_LENGTH}, máx: ${COMMENT_MAX_LENGTH})`;
  };

  const isSubmissionReady = () => {
    const overallComments = watchedValues.overallComments;

    const hasAllCriteriaScores = evaluationCriteria.every((criterion) => {
      const evaluation = criteriaEvaluations[criterion.id];
      return (
        evaluation &&
        evaluation.score !== undefined &&
        evaluation.score !== null &&
        evaluation.score !== ''
      );
    });
    const hasAllCriteriaComments = evaluationCriteria.every((criterion) => {
      const evaluation = criteriaEvaluations[criterion.id];
      return (
        evaluation &&
        evaluation.comments &&
        evaluation.comments.trim() !== '' &&
        evaluation.comments.trim().length >= COMMENT_MIN_LENGTH &&
        evaluation.comments.trim().length <= COMMENT_MAX_LENGTH
      );
    });

    // Budget comments are optional (P3): they do not gate submission.

    return (
      hasAllCriteriaScores &&
      hasAllCriteriaComments &&
      overallComments &&
      overallComments.trim() !== '' &&
      overallComments.trim().length >= COMMENT_MIN_LENGTH &&
      overallComments.trim().length <= COMMENT_MAX_LENGTH
    );
  };
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h3 className={styles.title}>Formulario de Evaluación</h3>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowScaleModal(true)}
            className={styles.scaleButton}
            icon="open_in_new"
            iconPos="right"
          >
            Ver escala de evaluación
          </Button>
        </div>

        <div className={styles.progress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <span className={styles.progressText}>
            {completedCriteria}/{totalCriteria} criterios completados
          </span>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.criteriaSection}>
          {evaluationCriteria.map((criterion) => {
            const criterionData = criteriaEvaluations[criterion.id] || {};
            const criterionErrors = errors?.criteriaEvaluations as any;
            const hasError = criterionErrors?.[criterion.id];
            const criterionTouched = (
              touchedFields?.criteriaEvaluations as any
            )?.[criterion.id];

            return (
              <div key={criterion.id} className={styles.criterionCard}>
                <div className={styles.criterionHeader}>
                  <div className={styles.criterionInfo}>
                    <h5 className={styles.criterionName}>{criterion.name}</h5>
                  </div>
                  <div className={styles.criterionToggleRow}>
                    <button
                      type="button"
                      className={styles.descriptionToggle}
                      onClick={() =>
                        setExpandedCriterionId(
                          expandedCriterionId === criterion.id
                            ? null
                            : criterion.id
                        )
                      }
                      aria-expanded={expandedCriterionId === criterion.id}
                    >
                      <i className="uc-icon icon-size--sm">info</i>
                      <span>
                        {expandedCriterionId === criterion.id
                          ? 'Ocultar'
                          : 'En qué consiste'}
                      </span>
                      <i className="uc-icon icon-size--sm">
                        {expandedCriterionId === criterion.id
                          ? 'expand_less'
                          : 'expand_more'}
                      </i>
                    </button>
                    <span className={styles.criterionWeight}>
                      Pondera un {criterion.weight}%
                    </span>
                  </div>
                  {expandedCriterionId === criterion.id && (
                    <div className={styles.criterionExpandedContent}>
                      <p className={styles.criterionDescription}>
                        {criterion.description}
                      </p>
                      <div className={styles.criterionDetailsGrid}>
                        <div className={styles.criterionDetailBlock}>
                          <h6 className={styles.criterionDetailTitle}>
                            Aspectos a Evaluar
                          </h6>
                          <ul className={styles.criterionDetailList}>
                            {criterion.aspects.map((aspect, i) => (
                              <li key={i}>{aspect}</li>
                            ))}
                          </ul>
                        </div>
                        <div className={styles.criterionDetailBlock}>
                          <h6 className={styles.criterionDetailTitle}>
                            Consideraciones
                          </h6>
                          <ul className={styles.criterionDetailList}>
                            {criterion.considerations.map((c, i) => (
                              <li key={i}>{c}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.criterionBody}>
                  <div className={styles.scoreSection}>
                    <label className={styles.scoreLabel}>
                      Puntuación (1-5):
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="5"
                      step="0.1"
                      className={`${styles.scoreInput} ${hasError?.score ? styles.inputError : ''}`}
                      placeholder="1.0"
                      onWheel={(e) => e.currentTarget.blur()}
                      onInput={(e) => {
                        const target = e.target as HTMLInputElement;
                        const value = parseFloat(target.value);
                        if (!isNaN(value)) {
                          if (value < 1) target.value = '1';
                          if (value > 5) target.value = '5';
                        }
                      }}
                      {...register(
                        `criteriaEvaluations.${criterion.id}.score`,
                        {
                          required: 'La puntuación es obligatoria',
                          min: {
                            value: 1,
                            message: 'La puntuación mínima es 1',
                          },
                          max: {
                            value: 5,
                            message: 'La puntuación máxima es 5',
                          },
                          validate: (value) => {
                            const numericValue = parseFloat(value);
                            if (isNaN(numericValue))
                              return 'Debe ingresar un número válido';
                            if (numericValue < 1 || numericValue > 5)
                              return 'La puntuación debe estar entre 1 y 5';
                            return true;
                          },
                        }
                      )}
                    />
                    {hasError?.score && (
                      <span className={styles.errorText}>
                        {hasError.score.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.commentsSection}>
                    <label className={styles.commentsLabel}>
                      Justificación (obligatoria):
                    </label>
                    <textarea
                      className={getTextareaClass(
                        criterionData.comments?.length || 0,
                        !!hasError?.comments,
                        !!criterionTouched?.comments
                      )}
                      placeholder="Proporcione comentarios detallados que justifiquen su puntuación..."
                      rows={4}
                      {...register(
                        `criteriaEvaluations.${criterion.id}.comments`,
                        {
                          required: 'Los comentarios son obligatorios',
                          minLength: {
                            value: COMMENT_MIN_LENGTH,
                            message: `Los comentarios deben tener al menos ${COMMENT_MIN_LENGTH} caracteres`,
                          },
                          maxLength: {
                            value: COMMENT_MAX_LENGTH,
                            message: `Los comentarios no pueden exceder ${COMMENT_MAX_LENGTH} caracteres`,
                          },
                        }
                      )}
                    />
                    {hasError?.comments && criterionTouched?.comments && (
                      <span className={styles.errorText}>
                        {hasError.comments.message}
                      </span>
                    )}
                    <div
                      className={`${styles.commentsCount} ${getCharacterCountClass(criterionData.comments?.length || 0)}`}
                    >
                      {getCharacterCountText(
                        criterionData.comments?.length || 0
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Un solo rótulo por bloque (el label), consistente con Percepción
            global — antes había h4 + label duplicados. */}
        <div className={styles.budgetCommentsSection}>
          <div className={styles.budgetCommentsCard}>
            <div className={styles.budgetCommentsTextSection}>
              <label className={styles.commentsBlockTitle}>
                Comentarios sobre el presupuesto (opcional)
              </label>
              <textarea
                className={`${styles.commentsInput} ${
                  errors?.budgetComments && touchedFields?.budgetComments
                    ? styles.inputError
                    : ''
                }`.trim()}
                placeholder="Si lo estima necesario, agregue comentarios sobre el presupuesto del proyecto..."
                rows={4}
                {...register('budgetComments', {
                  maxLength: {
                    value: BUDGET_COMMENT_MAX_LENGTH,
                    message: `Los comentarios no pueden exceder ${BUDGET_COMMENT_MAX_LENGTH} caracteres`,
                  },
                })}
              />
              {errors?.budgetComments && touchedFields?.budgetComments && (
                <span className={styles.errorText}>
                  {(errors.budgetComments as any)?.message}
                </span>
              )}
              <div
                className={`${styles.commentsCount} ${getCharacterCountClass(watchedValues.budgetComments?.length || 0)}`}
              >
                {getCharacterCountText(
                  watchedValues.budgetComments?.length || 0,
                  true
                )}
              </div>
            </div>
          </div>
        </div>

        {/* La "Recomendación General" (aprobar/rechazar) fue eliminada por
            definición del equipo (P5, 2026-07-14): la evaluación se expresa
            solo en puntajes + percepción global. */}
        <div className={styles.recommendationSection}>
          <div className={styles.recommendationCard}>
            <div className={styles.generalCommentsSection}>
              <label className={styles.commentsBlockTitle}>
                Percepción global de la propuesta *
              </label>
              <p className={styles.commentsHint}>
                Resuma brevemente su percepción global de la propuesta. No es la
                suma de los comentarios por criterio.
              </p>
              <textarea
                className={getTextareaClass(
                  watchedValues.overallComments?.length || 0,
                  !!errors?.overallComments,
                  !!touchedFields?.overallComments
                )}
                placeholder="Resuma su percepción global de la propuesta..."
                rows={6}
                {...register('overallComments', {
                  required:
                    'La percepción global de la propuesta es obligatoria',
                  minLength: {
                    value: COMMENT_MIN_LENGTH,
                    message: `Los comentarios deben tener al menos ${COMMENT_MIN_LENGTH} caracteres`,
                  },
                  maxLength: {
                    value: COMMENT_MAX_LENGTH,
                    message: `Los comentarios no pueden exceder ${COMMENT_MAX_LENGTH} caracteres`,
                  },
                })}
              />
              {errors?.overallComments && touchedFields?.overallComments && (
                <span className={styles.errorText}>
                  {(errors.overallComments as any)?.message}
                </span>
              )}
              <div
                className={`${styles.commentsCount} ${getCharacterCountClass(watchedValues.overallComments?.length || 0)}`}
              >
                {getCharacterCountText(
                  watchedValues.overallComments?.length || 0
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.submissionStatus}>
          {evaluation?.submittedAt ? (
            <Alert variant="success">
              Evaluación enviada el{' '}
              {new Date(evaluation.submittedAt).toLocaleDateString('es-CL')}.
            </Alert>
          ) : isSubmissionReady() ? null : (
            <Alert variant="warning">
              Complete todos los criterios para poder enviar la evaluación.
            </Alert>
          )}
        </div>
      </div>

      {/* Modals */}
      {showScaleModal && (
        <EvaluationScaleModal onClose={() => setShowScaleModal(false)} />
      )}
    </div>
  );
};

export default EvaluationPanel;
